// src/components/CardV2/media/StandardCard.tsx
/**
 * StandardCard — layout card for non-canonical content in standard (padded) feeds.
 * Content area has rounded corners with gradient+noise bg; header/footer sit outside.
 */

import React, { FC, useState } from 'react';
import { styled } from 'linaria/react';

import { FONTS } from '../../../styles/constants';
import { LayoutCardProps } from '../types';
import { deriveGradient } from '../primitives/colorUtils';
import { LAYOUT_CARD } from '../styled/tokens';

// -- Styled Components --

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
`;

const StyledLink = styled.a`
  display: block;
  text-decoration: none;
  cursor: pointer;
  color: inherit;
`;

const StyledContentArea = styled.div<{ gradientFrom: string; gradientTo: string }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  border-radius: ${LAYOUT_CARD.CONTENT_BORDER_RADIUS}px;
  overflow: hidden;
  position: relative;
  padding: ${LAYOUT_CARD.STANDARD_CONTENT_PADDING.top}px ${LAYOUT_CARD.STANDARD_CONTENT_PADDING.sides}px
    ${LAYOUT_CARD.STANDARD_CONTENT_PADDING.bottom}px;
  background: linear-gradient(180deg, ${({ gradientFrom }) => gradientFrom} 0%, ${({ gradientTo }) => gradientTo} 100%);
`;

// Noise texture: inline SVG data URI for zero network requests.
const NOISE_DATA_URI = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100' height='100' filter='url(%23n)' opacity='0.15'/%3E%3C/svg%3E")`;

const StyledNoiseOverlay = styled.div`
  position: absolute;
  inset: 0;
  background-image: ${NOISE_DATA_URI};
  background-repeat: repeat;
  opacity: ${LAYOUT_CARD.NOISE_OPACITY};
  pointer-events: none;
`;

const StyledEyebrow = styled.div`
  font-family: ${FONTS.secondary};
  font-size: ${LAYOUT_CARD.EYEBROW_FONT_SIZE}px;
  font-weight: 400;
  line-height: 1.4;
  color: white;
`;

const StyledTitle = styled.div`
  font-family: ${FONTS.primary};
  font-size: ${LAYOUT_CARD.TITLE_FONT_SIZE.standard}px;
  font-weight: 400;
  line-height: 1.3;
  color: white;
  position: relative;
  z-index: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const StyledTitleExpanded = styled(StyledTitle)`
  flex: 1;
`;

const StyledImageFrame = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: ${LAYOUT_CARD.IMAGE_BORDER_RADIUS}px;
  overflow: hidden;
  position: relative;
  z-index: 1;
`;

const StyledImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const StyledHeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  position: relative;
  z-index: 1;
`;

// -- Component --
// Note: StandardCard does NOT render its own header or footer.
// The CardV2 dispatcher renders CardHeader above and CardFooterMenu below
// (layoutManagesChrome is false for standard layout).

const StandardCard: FC<LayoutCardProps> = ({ title, imageUrl, backgroundColor, externalLink, eyebrow, className }) => {
  const gradient = deriveGradient(backgroundColor);
  const [imgError, setImgError] = useState(false);
  const showImage = !!imageUrl && !imgError;
  const safeImageUrl = imageUrl as string;

  const contentArea = (
    <StyledContentArea gradientFrom={gradient.from} gradientTo={gradient.to}>
      <StyledNoiseOverlay />
      <StyledHeaderSection>
        {eyebrow && <StyledEyebrow>{eyebrow}</StyledEyebrow>}
        {showImage ? <StyledTitle>{title}</StyledTitle> : <StyledTitleExpanded>{title}</StyledTitleExpanded>}
      </StyledHeaderSection>
      {showImage && (
        <StyledImageFrame>
          <StyledImage src={safeImageUrl} alt={title} loading="lazy" onError={() => setImgError(true)} />
        </StyledImageFrame>
      )}
    </StyledContentArea>
  );

  return (
    <StyledContainer className={className}>
      {externalLink ? (
        <StyledLink href={externalLink} target="_blank" rel="noopener noreferrer">
          {contentArea}
        </StyledLink>
      ) : (
        contentArea
      )}
    </StyledContainer>
  );
};

export default StandardCard;
